/**
 * @file: MediasRenderer.tsx
 *
 * A media gallery (`medias` payload). The bubble layout follows the sender's
 * chosen `payload.layout` — `stack` (z-stacked cards, the default when absent)
 * or `grid` (3-col grid, capped at 9 with a `+N` overlay). `quoted` mode
 * always shows the first item with a `+N` overlay. Each tile is its own
 * component so its press handler is memoized.
 */

import { Typography } from '@euler/app/components/typography';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMediaItem, ChatMessagePayloadMedias } from '@euler/model/chat';
import { Play } from 'lucide-react-native';
import { FC, ReactNode, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { stripHash } from './mediaLayout';
import { PayloadRendererProps } from './types';

interface Props extends PayloadRendererProps<ChatMessagePayloadMedias> {
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
}

/** Grid caps at 9 tiles; the rest collapse into a `+N` overlay on the last. */
const GRID_MAX = 9;

const PlayBadge: FC<{ size: number }> = memo(({ size }) => (
  <View
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: 'rgba(0,0,0,0.45)',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Play size={size * 0.55} color="#fff" fill="#fff" />
    </View>
  </View>
));

const CountOverlay: FC<{ text: string; fontSize: number }> = memo(
  ({ text, fontSize }) => (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.4)',
      }}
    >
      <Typography.Text size={fontSize} color="#fff">
        {text}
      </Typography.Text>
    </View>
  ),
);

const MediaTile: FC<{
  item: ChatMediaItem;
  width: number;
  height: number;
  radius: number;
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
  children?: ReactNode;
}> = memo(
  ({
    item,
    width,
    height,
    radius,
    onPreviewImage,
    onPreviewVideo,
    children,
  }) => {
    const handlePress = useCallback(() => {
      const url = stripHash(item.url);
      if (item.kind === 'video') onPreviewVideo?.(url);
      else onPreviewImage?.(url);
    }, [item, onPreviewVideo, onPreviewImage]);

    return (
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={handlePress}
        style={{
          width,
          height,
          borderRadius: radius,
          overflow: 'hidden',
          backgroundColor: '#e5e7eb',
        }}
      >
        <AdvancedImage
          uri={item.coverUrl ?? item.url}
          style={{ width, height }}
        />
        {item.kind === 'video' ? (
          <PlayBadge size={Math.min(width, height) * 0.34} />
        ) : null}
        {children}
      </TouchableOpacity>
    );
  },
);

/** Z-stacked cards: the first item on top, the rest peeking behind it. */
const StackMedias: FC<{
  items: ChatMediaItem[];
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
}> = memo(({ items, onPreviewImage, onPreviewVideo }) => {
  const { rpx } = useResponsiveContext();
  const side = rpx(150);
  const radius = rpx(10);
  const offset = rpx(5);
  const top = items[0];
  const peek = Math.min(items.length - 1, 2);

  const handlePress = useCallback(() => {
    if (!top) return;
    const url = stripHash(top.url);
    if (top.kind === 'video') onPreviewVideo?.(url);
    else onPreviewImage?.(url);
  }, [top, onPreviewImage, onPreviewVideo]);

  if (!top) return null;

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={handlePress}
      style={{ width: side + offset * peek, height: side + offset * peek }}
    >
      {/* Peeking cards behind, deepest first (later siblings render on top). */}
      {Array.from({ length: peek }).map((_, i) => {
        const depth = peek - i; // peek, …, 1
        return (
          <View
            key={i}
            style={{
              position: 'absolute',
              top: offset * depth,
              left: offset * depth,
              width: side,
              height: side,
              borderRadius: radius,
              backgroundColor: '#cbd5e1',
              borderWidth: 2,
              borderColor: '#fff',
            }}
          />
        );
      })}
      <View
        style={{
          width: side,
          height: side,
          borderRadius: radius,
          overflow: 'hidden',
          backgroundColor: '#e5e7eb',
        }}
      >
        <AdvancedImage
          uri={top.coverUrl ?? top.url}
          style={{ width: side, height: side }}
        />
        {top.kind === 'video' ? <PlayBadge size={side * 0.28} /> : null}
        <View
          style={{
            position: 'absolute',
            right: rpx(8),
            bottom: rpx(8),
            paddingHorizontal: rpx(8),
            paddingVertical: rpx(2),
            borderRadius: rpx(10),
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
        >
          <Typography.Text size={rpx(11)} color="#fff">
            {items.length}
          </Typography.Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

/** 3-col grid, capped at 9 tiles with the overflow folded into a `+N` tile. */
const GridMedias: FC<{
  items: ChatMediaItem[];
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
}> = memo(({ items, onPreviewImage, onPreviewVideo }) => {
  const { rpx } = useResponsiveContext();
  // Pixel-rounded, fully fixed dimensions: a sub-pixel tile size or a
  // negotiated container box (maxWidth + flexWrap) reflows the grid as images
  // load, which reads as jank. Lock the tile AND the container's width+height
  // from the (capped) item count so the box never resizes.
  const side = Math.round(rpx(78));
  const gap = Math.round(rpx(4));
  const cols = 3;
  const shown = items.slice(0, GRID_MAX);
  const overflow = items.length - GRID_MAX;
  const rows = Math.max(1, Math.ceil(shown.length / cols));
  const width = side * cols + gap * (cols - 1);
  const height = side * rows + gap * (rows - 1);
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap,
        width,
        height,
      }}
    >
      {shown.map((it, i) => (
        <MediaTile
          key={it.url || i}
          item={it}
          width={side}
          height={side}
          radius={rpx(6)}
          onPreviewImage={onPreviewImage}
          onPreviewVideo={onPreviewVideo}
        >
          {i === GRID_MAX - 1 && overflow > 0 ? (
            <CountOverlay text={`+${overflow}`} fontSize={rpx(15)} />
          ) : null}
        </MediaTile>
      ))}
    </View>
  );
});

export const MediasRenderer: FC<Props> = memo(
  ({ payload, quoted, onPreviewImage, onPreviewVideo }) => {
    const { rpx } = useResponsiveContext();
    const items = payload.items;

    if (quoted) {
      const side = rpx(40);
      const first = items[0];
      const extra = items.length - 1;
      if (!first) return null;
      return (
        <MediaTile
          item={first}
          width={side}
          height={side}
          radius={rpx(6)}
          onPreviewImage={onPreviewImage}
          onPreviewVideo={onPreviewVideo}
        >
          {extra > 0 ? (
            <CountOverlay text={`+${extra}`} fontSize={rpx(13)} />
          ) : null}
        </MediaTile>
      );
    }

    // Absent layout (older messages / other clients) renders as `stack`.
    const layout = payload.layout ?? 'stack';
    if (layout === 'grid') {
      return (
        <GridMedias
          items={items}
          onPreviewImage={onPreviewImage}
          onPreviewVideo={onPreviewVideo}
        />
      );
    }
    return (
      <StackMedias
        items={items}
        onPreviewImage={onPreviewImage}
        onPreviewVideo={onPreviewVideo}
      />
    );
  },
);
